import React, { useState, useEffect } from 'react';
import { CiUser } from 'react-icons/ci';
import { LiaUserLockSolid } from 'react-icons/lia';
import { FiMail, FiCheckCircle, FiLock } from 'react-icons/fi';
import { MdOutlinePeople } from 'react-icons/md';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchEmployeeById, updateEmployee } from '../redux/actions/employeeActions';
import { fetchUsers } from '../redux/actions/userActions';
import toast from 'react-hot-toast';
import ComponentWrapper from '../components/utils/ComponentWrapper';
import Loading from '../components/utils/Loading';

// Interface for form errors
interface FormErrors {
  email?: string;
  username?: string;
  password?: string;
  designationId?: string;
  employeeStatus?: string;
}

const EditUser: React.FC = () => {
  // Get user ID from URL params
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get user data from Redux store
  const { currentEmployee, loading, error } = useAppSelector(state => state.employees);
  const { designations, hasFetched, loading: usersLoading } = useAppSelector(state => state.users);

  // Form state
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [userType, setUserType] = useState('');
  const [status, setStatus] = useState('');

  // Validation state
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Fetch user data on component mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchEmployeeById(parseInt(userId)))
        .unwrap()
        .catch(error => {
          toast.error(`Error fetching user data: ${error}`);
        });
    }
  }, [dispatch, userId]);

  // Fetch designations if not already loaded
  useEffect(() => {
    if ((!designations || designations.length === 0) && !hasFetched && !usersLoading) {
      console.log('EditUser: Fetching users/designations data');
      dispatch(fetchUsers());
    }
  }, [dispatch, designations, hasFetched, usersLoading]);

  // Populate form when user data is loaded
  useEffect(() => {
    if (currentEmployee) {
      setEmail(currentEmployee.email || '');
      setUsername(currentEmployee.fullname || '');
      setUserType(currentEmployee.designationId || '');
      setStatus(currentEmployee.employeeStatus || 'ACTIVE');
      // Password field is left empty by default
    }
  }, [currentEmployee]);

  // Validate form fields
  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'email':
        if (!value) return 'Email is required';
        if (!/\S+@\S+\.\S+/.test(value)) return 'Email is invalid';
        return undefined;

      case 'username':
        if (!value) return 'Name is required';
        if (value.length < 3) return 'Name must be at least 3 characters';
        return undefined;

      case 'password':
        // Password is optional for existing user
        if (value && value.length < 6) return 'Password must be at least 6 characters';
        return undefined;

      case 'userType':
        if (!value) return 'User type is required';
        return undefined;

      case 'status':
        if (!value) return 'Status is required';
        return undefined;

      default:
        return undefined;
    }
  };

  // Handle field blur for validation
  const handleBlur = (field: string, value: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const error = validateField(field, value);
    setErrors(prev => ({ ...prev, [field]: error }));
  };

  // Validate all fields
  const validateForm = () => {
    const newErrors: FormErrors = {};
    let isValid = true;

    // We only validate fields that are required or have been changed
    newErrors.email = validateField('email', email);
    newErrors.username = validateField('username', username);
    if (password) {
      newErrors.password = validateField('password', password);
    }
    newErrors.designationId = validateField('userType', userType);
    newErrors.employeeStatus = validateField('status', status);

    // Check for any validation errors
    for (const error of Object.values(newErrors)) {
      if (error) {
        isValid = false;
        break;
      }
    }

    setErrors(newErrors);
    // Mark all fields as touched
    setTouched({
      email: true,
      username: true,
      password: Boolean(password),
      userType: true,
      status: true,
    });

    return isValid;
  };

  // Function to get designation name from ID
  const getDesignationName = (designationId: string) => {
    if (!designationId || !designations || designations.length === 0) return '';
    const selectedDesignation = designations.find(d => d.id === designationId);
    return selectedDesignation ? selectedDesignation.name : '';
  };

  // Function to get designation ID from name
  const getDesignationId = (designationName: string) => {
    if (!designationName || !designations || designations.length === 0) return '';
    const selectedDesignation = designations.find(d => d.name === designationName);
    return selectedDesignation ? selectedDesignation.id : '';
  };

  // Handle form submission
  const handleSubmit = () => {
    if (!validateForm()) {
      toast.error('Please fix the form errors before saving');
      return;
    }

    // Check which fields have been changed
    const changedFields: any = {};

    if (email !== currentEmployee?.email) changedFields.email = email;
    if (username !== currentEmployee?.fullname) changedFields.fullname = username;
    if (password) changedFields.password = password;
    if (userType !== currentEmployee?.designationId) changedFields.designationId = userType;
    if (status !== currentEmployee?.employeeStatus) changedFields.employeeStatus = status;

    // If no fields changed, show message
    if (Object.keys(changedFields).length === 0) {
      toast.info('No changes detected');
      return;
    }

    // Show loading toast
    const loadingToast = toast.loading('Updating user...');

    // Update the user
    dispatch(updateEmployee({ id: parseInt(userId!), data: changedFields }))
      .unwrap()
      .then(() => {
        toast.dismiss(loadingToast);
        toast.success('User updated successfully!');
        navigate('/users');
      })
      .catch(error => {
        toast.dismiss(loadingToast);
        toast.error(`Failed to update user: ${error}`);
      });
  };

  // Create user type options from designations
  const userTypeOptions = designations ? ['', ...designations.map(d => d.name)] : [''];

  // Status options - all possible employee statuses
  const statusOptions = [
    'ACTIVE',
    'INACTIVE',
    'PROBATION',
    'TERMINATED',
    'RESIGNED',
    'RETIRED',
    'ON_LEAVE',
  ];

  // Format status for display
  const formatStatus = (status: string): string => {
    if (!status) return 'Unknown';

    return status
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get color for status display
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-50 text-green-700';
      case 'INACTIVE':
        return 'bg-red-50 text-red-700';
      case 'PROBATION':
        return 'bg-yellow-50 text-yellow-700';
      case 'TERMINATED':
        return 'bg-gray-50 text-gray-700';
      case 'RESIGNED':
        return 'bg-orange-50 text-orange-700';
      case 'RETIRED':
        return 'bg-blue-50 text-blue-700';
      case 'ON_LEAVE':
        return 'bg-purple-50 text-purple-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  if (loading && !currentEmployee) {
    return (
      <ComponentWrapper icon={<MdOutlinePeople />} title="Edit User">
         <div className="flex justify-center items-center ">
              <Loading height='400px'/>
            </div>
      </ComponentWrapper>
    );
  }

  if (error) {
    return (
      <ComponentWrapper icon={<MdOutlinePeople />} title="Edit User">
        <div className="text-center py-6">
          <p className="text-red-500">Error loading user data. Please try again.</p>
        </div>
      </ComponentWrapper>
    );
  }

  return (
    <ComponentWrapper icon={<MdOutlinePeople />} title="Edit User">
      <div className="w-full bg-white rounded-lg shadow-sm p-6">
        <div className="flex flex-col gap-6 max-w-3xl mx-auto">
          {/* Email and Password in one row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Email Field */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">User Email</label>
              <div className={`relative ${touched.email && errors.email ? 'mb-1' : ''}`}>
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <FiMail className="w-5 h-5" />
                </span>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  onBlur={() => handleBlur('email', email)}
                  disabled={true}
                  className="w-full py-2.5 pl-10 pr-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent bg-gray-100 text-gray-700"
                />
              </div>
              {touched.email && errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                User's Password
              </label>
              <div className={`relative ${touched.password && errors.password ? 'mb-1' : ''}`}>
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <FiLock className="w-5 h-5" />
                </span>
                <input
                  type="password"
                  placeholder="Enter new password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  onBlur={() => handleBlur('password', password)}
                  className="w-full py-2.5 pl-10 pr-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              {touched.password && errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
          </div>

          {/* Username and User Type in one row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Username Field */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">User Name</label>
              <div className={`relative ${touched.username && errors.username ? 'mb-1' : ''}`}>
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <CiUser className="w-5 h-5" />
                </span>
                <input
                  type="text"
                  placeholder="Enter user name"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  onBlur={() => handleBlur('username', username)}
                  className="w-full py-2.5 pl-10 pr-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              {touched.username && errors.username && (
                <p className="text-red-500 text-xs mt-1">{errors.username}</p>
              )}
            </div>

            {/* User Type Field */}
            <div>
              <label className="block mb-2 text-sm font-medium text-gray-700">
                Select User Type
              </label>
              <div className={`relative ${touched.userType && errors.designationId ? 'mb-1' : ''}`}>
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <LiaUserLockSolid className="w-5 h-5" />
                </span>
                <select
                  value={getDesignationName(userType)}
                  onChange={e => {
                    const designationId = getDesignationId(e.target.value);
                    setUserType(designationId);
                    handleBlur('userType', designationId);
                  }}
                  className="w-full py-2.5 pl-10 pr-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent appearance-none"
                >
                  {userTypeOptions.map((option, index) => (
                    <option key={index} value={option}>
                      {option || 'Select user type'}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg
                    className="fill-current h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                  >
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>
              {touched.userType && errors.designationId && (
                <p className="text-red-500 text-xs mt-1">{errors.designationId}</p>
              )}
            </div>
          </div>

          {/* Status Field - styled to match the image */}
          <div className="mb-1">
            <label className="block mb-2 text-sm font-medium text-gray-700">Status</label>
            <div
              className={`${getStatusColor(status)} border border-gray-200 rounded-lg relative ${touched.status && errors.employeeStatus ? 'border-red-500' : ''}`}
            >
              <div className="flex items-center px-4 py-3">
                <FiCheckCircle
                  className={`w-5 h-5 ${status === 'ACTIVE' ? 'text-green-600' : status === 'INACTIVE' ? 'text-red-600' : 'text-gray-400'}`}
                />
                <span className="ml-2 font-medium">{formatStatus(status)}</span>
                <div className="ml-auto">
                  <select
                    className="appearance-none bg-transparent border-none focus:outline-none cursor-pointer pr-8 text-sm"
                    value={status}
                    onChange={e => {
                      setStatus(e.target.value);
                      handleBlur('status', e.target.value);
                    }}
                  >
                    {statusOptions.map(option => (
                      <option key={option} value={option} className="text-gray-700">
                        {formatStatus(option)}
                      </option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-4">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
            {touched.status && errors.employeeStatus && (
              <p className="text-red-500 text-xs mt-1">{errors.employeeStatus}</p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <button
              onClick={handleSubmit}
              className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center font-medium"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </ComponentWrapper>
  );
};

export default EditUser;
